import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
  Paper, TablePagination, TextField, Button, Box, Typography,
  Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  useTheme, useMediaQuery, Tooltip, IconButton,
  Badge, CircularProgress, Checkbox, Radio, RadioGroup, FormControlLabel, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Add as AddIcon,
  AddShoppingCart as AddShoppingCartIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useInventoryShell } from '../context/InventoryShellContext';
import { useFeedback } from '../context/FeedbackContext';
import { useAuth } from '../context/AuthContext';
import { useCartActions } from '../context/CartContext';
import { inventoryTabPropsAreEqual } from '../utils/inventoryTabMemo';
import { ROLES } from '../constants/roles';
import { DIAMOND_TYPES, ORDER_MODES } from '../constants/appConstants';
import axios from 'axios';
import { TABLE_ROWS_PREF_KEYS, pickRowsPerPageFromPrefs, saveTableRowsPreference } from '../utils/tablePagePreferences';
import { parseLotsSearchParams, buildLotsSearchParams, serializeSearchParamsSorted, isLotsSortColumn } from '../utils/inventoryUrlParams';
import AuditLogDialog from '../components/inventory/AuditLogDialog';
import UploadCsvDialog from '../components/inventory/UploadCsvDialog';
import QuickFilterBar from '../components/inventory/QuickFilterBar';
import ReserveLotDialog from '../components/inventory/ReserveLotDialog';
import EditLotDialog from '../components/inventory/EditLotDialog';
import AddLotDialog from '../components/inventory/AddLotDialog';
import LotsFilterDrawer, { DEFAULT_LOTS_FILTERS } from '../components/inventory/LotsFilterDrawer';
import { VIEWPORT_TABLE_MAX_HEIGHT } from '../components/ResponsiveTable';
import MobileTableSort from '../components/MobileTableSort';
import { useTranslation } from 'react-i18next';
import SelectField from '../components/inputs/SelectField';
import {
  formatLotAvailableForClient,
  getNonCertLotClientCardTitle,
} from '../utils/nonCertLotDisplay';

import { resolveInventorySettings, resolveClientShapeSelection, resolveClientDefaultShapeFilter, resolveLotAllowOrderOverStock } from '../utils/inventorySettings';
import { resolveShapeOptions, inventoryShapeOptions } from '../utils/shapeOptions';
import { hasPermission } from '../utils/permissions';

const LotsInventory = ({ isTabActive = true, searchParams, setSearchParams }) => {
  const { t } = useTranslation();
  const { isInternal, isClient, userRole, customPermissions, company, loading: authLoading } = useAuth();
  const inventorySettings = resolveInventorySettings(company);
  const lotsSettings = inventorySettings.lots;
  const { addToCart } = useCartActions();
  const { showSuccess } = useFeedback();
  const { filterOptions } = useInventoryShell();
  const lotInventoryShapes = filterOptions.lotShapes ?? filterOptions.shapes;
  const clientShapeList = useMemo(
    () => inventoryShapeOptions(lotInventoryShapes),
    [lotInventoryShapes],
  );
  const filterShapeList = clientShapeList;
  const formShapeList = useMemo(
    () => resolveShapeOptions(lotInventoryShapes, company?.customShapes),
    [lotInventoryShapes, company?.customShapes],
  );
  const configuredClientDefault = useMemo(
    () => (isClient ? resolveClientDefaultShapeFilter(company, 'lots') : ''),
    [isClient, company],
  );
  const clientDefaultShape = useMemo(
    () => (isClient ? resolveClientShapeSelection(configuredClientDefault, clientShapeList) : ''),
    [isClient, configuredClientDefault, clientShapeList],
  );

  const canAddToCart = isInternal
    ? (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('reserve_for_client')))
    : userRole === ROLES.CLIENT;
  const canEditDiamond = isInternal && (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('edit_diamond')));
  const canAddDiamond = isInternal && (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('add_diamond')));
  const isManager = userRole === ROLES.MANAGER;
  const canSeeBuyPrices = hasPermission(userRole, 'see_buy_prices', customPermissions);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const skipUrlHydrationRef = useRef(false);
  const showAvailableToClients = lotsSettings.showAvailableWeightToClients !== false;

  const fetchLotsSeqRef = useRef(0);
  const defaultsAppliedRef = useRef(false);
  const suppressListFetchRef = useRef(false);

  // ── Lots state ────────────────────────────────────────────────────────────
  const [lots, setLots] = useState([]);
  const [lotsPage, setLotsPage] = useState(0);
  const [lotsRowsPerPage, setLotsRowsPerPage] = useState(10);
  const [lotsTotal, setLotsTotal] = useState(0);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotsFilterShape, setLotsFilterShape] = useState('');
  const [lotsAvailableOnly, setLotsAvailableOnly] = useState(true);
  const [lotsMinCaratPerStone, setLotsMinCaratPerStone] = useState('');
  const [lotsMaxCaratPerStone, setLotsMaxCaratPerStone] = useState('');
  const [lotsMinLength, setLotsMinLength] = useState('');
  const [lotsMaxLength, setLotsMaxLength] = useState('');
  const [lotsMinWidth, setLotsMinWidth] = useState('');
  const [lotsMaxWidth, setLotsMaxWidth] = useState('');
  const [lotsSortBy, setLotsSortBy] = useState('');
  const [lotsSortDir, setLotsSortDir] = useState('asc');
  const [qfShape, setQfShape] = useState('');
  const [qfWeight, setQfWeight] = useState('');
  const [qfLength, setQfLength] = useState('');

  // ── Dialogs ───────────────────────────────────────────────────────────────
  const [reserveLotDialogOpen, setReserveLotDialogOpen] = useState(false);
  const [reserveLot, setReserveLot] = useState(null);
  const [addLotDialogOpen, setAddLotDialogOpen] = useState(false);
  const [editLot, setEditLot] = useState(null);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // ── Bulk edit (manager) ───────────────────────────────────────────────────
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkEditStep, setBulkEditStep] = useState(1);
  const [bulkEditUseFilter, setBulkEditUseFilter] = useState(false);
  const [bulkEditCount, setBulkEditCount] = useState(0);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkEditConfirmOpen, setBulkEditConfirmOpen] = useState(false);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkEditError, setBulkEditError] = useState('');

  // ── Audit log ─────────────────────────────────────────────────────────────
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTitle, setAuditTitle] = useState('');

  // ── URL snapshot ──────────────────────────────────────────────────────────

  const getUrlSnapshot = useCallback(() => ({
    page: lotsPage, rowsPerPage: lotsRowsPerPage, shape: lotsFilterShape,
    availableOnly: lotsAvailableOnly, minCaratPerStone: lotsMinCaratPerStone,
    maxCaratPerStone: lotsMaxCaratPerStone, minLength: lotsMinLength,
    maxLength: lotsMaxLength, minWidth: lotsMinWidth, maxWidth: lotsMaxWidth,
    defaultRows: 10,
  }), [lotsPage, lotsRowsPerPage, lotsFilterShape, lotsAvailableOnly, lotsMinCaratPerStone, lotsMaxCaratPerStone, lotsMinLength, lotsMaxLength, lotsMinWidth, lotsMaxWidth]);

  // ── Preferences ───────────────────────────────────────────────────────────

  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      let prefs = {};
      try {
        const response = await axios.get('/api/users/preferences');
        prefs = response.data || {};
      } catch { /* silent */ }
      setLotsRowsPerPage(pickRowsPerPageFromPrefs(prefs, TABLE_ROWS_PREF_KEYS.inventoryLots, [10, 25, 50, 100], 10));
      setPreferencesLoaded(true);
    };
    loadPreferences();
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchLots = useCallback(async (opts = {}) => {
    const {
      pageOverride,
      shape: shapeOverride,
      availableOnly: availOverride,
      minCaratPerStone: minCtOverride,
      maxCaratPerStone: maxCtOverride,
      minLength: minLenOverride,
      maxLength: maxLenOverride,
      minWidth: minWOverride,
      maxWidth: maxWOverride,
      rowsPerPage: limitOverride,
      sortBy: sortByOverride,
      sortDir: sortDirOverride,
    } = opts;
    const pageToUse = pageOverride !== undefined ? pageOverride : lotsPage;
    const limitToUse = limitOverride ?? lotsRowsPerPage;
    const shape = shapeOverride ?? lotsFilterShape;
    const avail = availOverride ?? lotsAvailableOnly;
    const minCt = minCtOverride ?? lotsMinCaratPerStone;
    const maxCt = maxCtOverride ?? lotsMaxCaratPerStone;
    const minLen = minLenOverride ?? lotsMinLength;
    const maxLen = maxLenOverride ?? lotsMaxLength;
    const minW = minWOverride ?? lotsMinWidth;
    const maxW = maxWOverride ?? lotsMaxWidth;
    const sortBy = sortByOverride !== undefined ? sortByOverride : lotsSortBy;
    const sortDir = sortDirOverride !== undefined ? sortDirOverride : lotsSortDir;
    const fetchSeq = ++fetchLotsSeqRef.current;

    setLotsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', (pageToUse + 1).toString());
      params.set('limit', String(limitToUse));
      params.set('availableOnly', avail ? 'true' : 'false');
      if (shape) params.set('shape', shape);
      if (minCt !== '' && minCt != null) params.set('minCaratPerStone', String(minCt));
      if (maxCt !== '' && maxCt != null) params.set('maxCaratPerStone', String(maxCt));
      if (minLen !== '' && minLen != null) params.set('minLength', String(minLen));
      if (maxLen !== '' && maxLen != null) params.set('maxLength', String(maxLen));
      if (minW !== '' && minW != null) params.set('minWidth', String(minW));
      if (maxW !== '' && maxW != null) params.set('maxWidth', String(maxW));
      if (sortBy && isLotsSortColumn(sortBy)) {
        params.set('sortBy', sortBy);
        params.set('sortDir', sortDir);
      }
      const response = await axios.get(`/api/non-certified-diamonds?${params}`);
      if (fetchSeq !== fetchLotsSeqRef.current) return;
      setLots(response.data.nonCertifiedDiamonds ?? []);
      setLotsTotal(response.data.pagination?.total ?? 0);
    } catch (error) {
      if (fetchSeq !== fetchLotsSeqRef.current) return;
      console.error('Failed to fetch non-certified lots:', error);
    } finally {
      if (fetchSeq === fetchLotsSeqRef.current) {
        setLotsLoading(false);
      }
    }
  }, [lotsPage, lotsRowsPerPage, lotsFilterShape, lotsAvailableOnly, lotsMinCaratPerStone, lotsMaxCaratPerStone, lotsMinLength, lotsMaxLength, lotsMinWidth, lotsMaxWidth, lotsSortBy, lotsSortDir]);

  // ── Filter handlers ───────────────────────────────────────────────────────

  const appliedLotsFilters = useMemo(() => ({
    shape: lotsFilterShape,
    availableOnly: lotsAvailableOnly,
    minCaratPerStone: lotsMinCaratPerStone,
    maxCaratPerStone: lotsMaxCaratPerStone,
    minLength: lotsMinLength,
    maxLength: lotsMaxLength,
    minWidth: lotsMinWidth,
    maxWidth: lotsMaxWidth,
  }), [
    lotsFilterShape, lotsAvailableOnly, lotsMinCaratPerStone, lotsMaxCaratPerStone,
    lotsMinLength, lotsMaxLength, lotsMinWidth, lotsMaxWidth,
  ]);

  const handleClearLotFilters = useCallback(() => {
    const resetShape = isClient ? clientDefaultShape : '';
    const d = { ...DEFAULT_LOTS_FILTERS, shape: resetShape };
    setLotsFilterShape(d.shape);
    setLotsAvailableOnly(d.availableOnly);
    setLotsMinCaratPerStone(d.minCaratPerStone);
    setLotsMaxCaratPerStone(d.maxCaratPerStone);
    setLotsMinLength(d.minLength);
    setLotsMaxLength(d.maxLength);
    setLotsMinWidth(d.minWidth);
    setLotsMaxWidth(d.maxWidth);
    setQfShape(resetShape);
    setQfWeight('');
    setQfLength('');
    setLotsPage(0);
    fetchLots({ ...d, pageOverride: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLots, isClient, clientDefaultShape]);

  const handleApplyLotFilters = useCallback((filters) => {
    const {
      shape, availableOnly, minCaratPerStone, maxCaratPerStone,
      minLength, maxLength, minWidth, maxWidth,
    } = filters;
    setLotsFilterShape(shape);
    setLotsAvailableOnly(availableOnly);
    setLotsMinCaratPerStone(minCaratPerStone);
    setLotsMaxCaratPerStone(maxCaratPerStone);
    setLotsMinLength(minLength);
    setLotsMaxLength(maxLength);
    setLotsMinWidth(minWidth);
    setLotsMaxWidth(maxWidth);
    setQfShape(shape || '');
    setLotsPage(0);
    fetchLots({
      shape, availableOnly, minCaratPerStone, maxCaratPerStone,
      minLength, maxLength, minWidth, maxWidth, pageOverride: 0,
    });
  }, [fetchLots]);

  // ── Active filter chips ───────────────────────────────────────────────────
  //
  // Lot filters apply immediately on change, so chips mirror current state.

  const activeLotsChips = useMemo(() => {
    const chips = [];
    if (lotsFilterShape)
      chips.push({ key: 'shape', label: `Shape: ${lotsFilterShape}` });
    if (!lotsAvailableOnly)
      chips.push({ key: 'availability', label: 'Show: All lots' });
    if (lotsMinCaratPerStone || lotsMaxCaratPerStone) {
      const label = lotsMinCaratPerStone && lotsMaxCaratPerStone
        ? `Ct/stone: ${lotsMinCaratPerStone}–${lotsMaxCaratPerStone}`
        : lotsMinCaratPerStone ? `Min ct/stone: ${lotsMinCaratPerStone}` : `Max ct/stone: ${lotsMaxCaratPerStone}`;
      chips.push({ key: 'carat', label });
    }
    if (lotsMinLength || lotsMaxLength) {
      const label = lotsMinLength && lotsMaxLength
        ? `Length: ${lotsMinLength}–${lotsMaxLength} mm`
        : lotsMinLength ? `Min length: ${lotsMinLength} mm` : `Max length: ${lotsMaxLength} mm`;
      chips.push({ key: 'length', label });
    }
    if (lotsMinWidth || lotsMaxWidth) {
      const label = lotsMinWidth && lotsMaxWidth
        ? `Width: ${lotsMinWidth}–${lotsMaxWidth} mm`
        : lotsMinWidth ? `Min width: ${lotsMinWidth} mm` : `Max width: ${lotsMaxWidth} mm`;
      chips.push({ key: 'width', label });
    }
    return chips;
  }, [lotsFilterShape, lotsAvailableOnly, lotsMinCaratPerStone, lotsMaxCaratPerStone, lotsMinLength, lotsMaxLength, lotsMinWidth, lotsMaxWidth]);

  const handleRemoveLotChip = useCallback((key) => {
    switch (key) {
      case 'shape': {
        const nextShape = isClient ? clientDefaultShape : '';
        setLotsFilterShape(nextShape);
        setQfShape(nextShape);
        setLotsPage(0);
        fetchLots({ shape: nextShape, pageOverride: 0 });
        break;
      }
      case 'availability':
        setLotsAvailableOnly(true);
        setLotsPage(0);
        fetchLots({ availableOnly: true, pageOverride: 0 });
        break;
      case 'carat':
        setLotsMinCaratPerStone('');
        setLotsMaxCaratPerStone('');
        setQfWeight('');
        setLotsPage(0);
        fetchLots({ minCaratPerStone: '', maxCaratPerStone: '', pageOverride: 0 });
        break;
      case 'length':
        setLotsMinLength('');
        setLotsMaxLength('');
        setQfLength('');
        setLotsPage(0);
        fetchLots({ minLength: '', maxLength: '', pageOverride: 0 });
        break;
      case 'width':
        setLotsMinWidth('');
        setLotsMaxWidth('');
        setLotsPage(0);
        fetchLots({ minWidth: '', maxWidth: '', pageOverride: 0 });
        break;
      default: break;
    }
  }, [fetchLots, isClient, clientDefaultShape]);

  // Deep-link / back-forward URL hydration
  useLayoutEffect(() => {
    if (!isTabActive) return;
    if (skipUrlHydrationRef.current) { skipUrlHydrationRef.current = false; return; }
    if (!searchParams.toString()) return;
    const parsed = parseLotsSearchParams(searchParams);
    setLotsFilterShape(parsed.shape);
    setLotsAvailableOnly(parsed.availableOnly);
    setLotsMinCaratPerStone(parsed.minCaratPerStone);
    setLotsMaxCaratPerStone(parsed.maxCaratPerStone);
    setLotsMinLength(parsed.minLength);
    setLotsMaxLength(parsed.maxLength);
    setLotsMinWidth(parsed.minWidth);
    setLotsMaxWidth(parsed.maxWidth);
    setQfShape(parsed.shape || '');
    setLotsPage(parsed.page);
    if (parsed.rowsPerPage != null) setLotsRowsPerPage(parsed.rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isTabActive]);

  // Keep URL in sync when filter/pagination changes
  useEffect(() => {
    if (!isTabActive) return undefined;
    const t = setTimeout(() => {
      const next = buildLotsSearchParams(getUrlSnapshot());
      if (serializeSearchParamsSorted(next) === serializeSearchParamsSorted(searchParams)) return;
      skipUrlHydrationRef.current = true;
      setSearchParams(next, { replace: true });
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotsPage, lotsRowsPerPage, lotsFilterShape, lotsAvailableOnly, lotsMinCaratPerStone, lotsMaxCaratPerStone, lotsMinLength, lotsMaxLength, lotsMinWidth, lotsMaxWidth, setSearchParams, isTabActive]);

  // Re-apply when company default shape settings refresh (e.g. after /api/auth/me)
  useEffect(() => {
    if (isClient) defaultsAppliedRef.current = false;
  }, [isClient, configuredClientDefault]);

  // Apply company default shape for clients on first load
  useEffect(() => {
    if (!isTabActive || !preferencesLoaded || authLoading) return;

    const urlShape = searchParams.toString()
      ? parseLotsSearchParams(searchParams).shape
      : '';

    if (!isClient) {
      defaultsAppliedRef.current = true;
      return;
    }

    if (urlShape) {
      defaultsAppliedRef.current = true;
      return;
    }

    if (defaultsAppliedRef.current) return;

    if (!configuredClientDefault && clientShapeList.length === 0) return;

    const targetShape = resolveClientShapeSelection(configuredClientDefault, clientShapeList);
    if (!targetShape) return;

    defaultsAppliedRef.current = true;
    suppressListFetchRef.current = true;
    setLotsFilterShape(targetShape);
    setQfShape(targetShape);
    fetchLots({ shape: targetShape, pageOverride: 0 });
  }, [
    isTabActive,
    preferencesLoaded,
    authLoading,
    isClient,
    configuredClientDefault,
    clientShapeList,
    searchParams,
    fetchLots,
  ]);

  // Refetch when page / rowsPerPage change (gated so it only runs after preferences load)
  useEffect(() => {
    if (!isTabActive || !preferencesLoaded || authLoading) return;
    if (isClient && !defaultsAppliedRef.current) return;
    if (suppressListFetchRef.current) {
      suppressListFetchRef.current = false;
      return;
    }
    fetchLots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotsPage, lotsRowsPerPage, preferencesLoaded, authLoading, isTabActive]);

  // ── Audit log ─────────────────────────────────────────────────────────────

  const openAuditLog = useCallback(async (type, id, title) => {
    setAuditTitle(title); setAuditDialogOpen(true); setAuditLogs([]); setAuditLoading(true);
    try {
      const params = { limit: 100 };
      if (type === 'lot') params.lot = id;
      const res = await axios.get('/api/audit-logs', { params });
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  // ── Reserve lot dialog ────────────────────────────────────────────────────

  const handleOpenAddLotToCartDialog = (lot) => {
    setReserveLot(lot);
    setReserveLotDialogOpen(true);
  };

  const handleCloseAddLotToCartDialog = () => {
    setReserveLotDialogOpen(false);
    setReserveLot(null);
  };

  const handleAddLotToCart = useCallback((lot, carat, meta = {}) => {
    addToCart({ type: DIAMOND_TYPES.NON_CERTIFIED, lot, weight: carat, ...meta });
    setReserveLotDialogOpen(false);
    setReserveLot(null);
    const successMsg = meta.orderMode === ORDER_MODES.BY_COUNT && meta.stoneCount
      ? t('inventory.lotAddedToCartByCount', { shape: lot.shape, count: meta.stoneCount, carat })
      : t('inventory.lotAddedToCart', { shape: lot.shape, carat });
    showSuccess(successMsg);
  }, [addToCart, showSuccess, t]);

  // ── Add lot ───────────────────────────────────────────────────────────────

  const handleAddLotSaved = useCallback(() => {
    fetchLots();
    showSuccess('Non-certified diamond added');
  }, [fetchLots, showSuccess]);

  // ── Edit lot ──────────────────────────────────────────────────────────────

  const openEditLot = useCallback((lot) => setEditLot(lot), []);
  const closeEditLot = useCallback(() => setEditLot(null), []);

  const handleEditLotSaved = useCallback(() => {
    fetchLots();
    showSuccess('Lot updated');
  }, [fetchLots, showSuccess]);

  // ── Filter badge ──────────────────────────────────────────────────────────

  const lotsFiltersActiveCount = useMemo(() => {
    let n = 0;
    if (lotsFilterShape) n += 1;
    if (!lotsAvailableOnly) n += 1;
    if (lotsMinCaratPerStone) n += 1;
    if (lotsMaxCaratPerStone) n += 1;
    if (lotsMinLength) n += 1;
    if (lotsMaxLength) n += 1;
    if (lotsMinWidth) n += 1;
    if (lotsMaxWidth) n += 1;
    return n;
  }, [lotsFilterShape, lotsAvailableOnly, lotsMinCaratPerStone, lotsMaxCaratPerStone, lotsMinLength, lotsMaxLength, lotsMinWidth, lotsMaxWidth]);

  // ── Quick filter ──────────────────────────────────────────────────────────

  const handleQuickLotFilterApply = useCallback(({ shape, weightCenter, minWeight, maxWeight, lengthCenter, minLength, maxLength }) => {
    setQfShape(shape);
    setQfWeight(weightCenter);
    setQfLength(lengthCenter);
    setLotsFilterShape(shape);
    setLotsMinCaratPerStone(minWeight);
    setLotsMaxCaratPerStone(maxWeight);
    setLotsMinLength(minLength);
    setLotsMaxLength(maxLength);
    setLotsPage(0);
    fetchLots({
      shape,
      minCaratPerStone: minWeight,
      maxCaratPerStone: maxWeight,
      minLength,
      maxLength,
      pageOverride: 0,
    });
  }, [fetchLots]);

  const handleQuickLotFilterClear = useCallback(() => {
    const resetShape = isClient ? clientDefaultShape : '';
    setQfShape(resetShape);
    setQfWeight('');
    setQfLength('');
    setLotsFilterShape(resetShape);
    setLotsMinCaratPerStone('');
    setLotsMaxCaratPerStone('');
    setLotsMinLength('');
    setLotsMaxLength('');
    setLotsPage(0);
    fetchLots({
      shape: resetShape,
      minCaratPerStone: '',
      maxCaratPerStone: '',
      minLength: '',
      maxLength: '',
      pageOverride: 0,
    });
  }, [fetchLots, isClient, clientDefaultShape]);

  // ── Sort ──────────────────────────────────────────────────────────────────

  const handleLotsSort = (field, direction) => {
    let newDir;
    if (direction) {
      newDir = direction;
    } else if (lotsSortBy === field) {
      newDir = lotsSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      newDir = 'asc';
    }
    setLotsSortBy(field);
    setLotsSortDir(newDir);
    setLotsPage(0);
    fetchLots({ sortBy: field, sortDir: newDir, pageOverride: 0 });
  };

  const handleLotsSortReset = useCallback(() => {
    setLotsSortBy('');
    setLotsSortDir('asc');
    setLotsPage(0);
    fetchLots({ sortBy: '', sortDir: 'asc', pageOverride: 0 });
  }, [fetchLots]);

  const lotsSortColumns = useMemo(() => [
    { id: 'shape', label: t('inventory.col.shape'), sortable: true },
    { id: 'length', label: t('inventory.col.measurement'), sortable: true },
    { id: 'caratPerStone', label: t('inventory.col.caratPerStone', 'Ct/stone'), sortable: true },
    { id: 'pricePerCarat', label: t('inventory.col.pricePerCarat'), sortable: true },
    { id: 'availableCarat', label: t('inventory.col.available'), sortable: true },
  ].filter((column) => isLotsSortColumn(column.id)), [t]);

  const sortCell = (field, label) => (
    <TableCell
      sortDirection={lotsSortBy === field ? lotsSortDir : false}
      sx={{ backgroundColor: 'background.paper' }}
    >
      <TableSortLabel
        active={lotsSortBy === field}
        direction={lotsSortBy === field ? lotsSortDir : 'asc'}
        onClick={() => handleLotsSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // ── Shapes list (resolved above for client defaults) ─────────────────────

  const handleOpenBulkEditFromFilters = useCallback(() => {
    setBulkEditOpen(true);
    setBulkEditStep(1);
    setBulkEditConfirmOpen(false);
    setBulkEditError('');
  }, []);

  const closeBulkEdit = useCallback(() => {
    setBulkEditOpen(false);
    setBulkSelectedIds([]);
    setBulkEditStep(1);
    setBulkEditUseFilter(false);
    setBulkEditField('');
    setBulkEditValue('');
    setBulkEditConfirmOpen(false);
    setBulkEditError('');
  }, []);

  const handleBulkEditUseFilter = () => {
    setBulkEditUseFilter(true);
    setBulkEditCount(lotsTotal);
    setBulkEditStep(2);
    setBulkEditError('');
  };

  const handleBulkEditNext = () => {
    if (bulkSelectedIds.length === 0) {
      setBulkEditError('Select at least one item or use "Update all by filter".');
      return;
    }
    setBulkEditUseFilter(false);
    setBulkEditCount(bulkSelectedIds.length);
    setBulkEditStep(2);
    setBulkEditError('');
  };

  const handleBulkEditFieldConfirm = () => {
    if (!bulkEditField) return;
    if (bulkEditField === 'allowOrderOverStock') {
      if (!bulkEditValue) return;
      setBulkEditConfirmOpen(true);
      return;
    }
    if (bulkEditValue === '' || Number(bulkEditValue) < 0) return;
    setBulkEditConfirmOpen(true);
  };

  const handleBulkEditSubmit = async () => {
    if (bulkEditLoading) return;
    setBulkEditLoading(true);
    setBulkEditError('');
    try {
      const bulkValue = bulkEditField === 'allowOrderOverStock'
        ? (bulkEditValue === 'true' ? true : (bulkEditValue === 'false' ? false : null))
        : Number(bulkEditValue);
      const payload = bulkEditUseFilter
        ? {
          filter: appliedLotsFilters,
          field: bulkEditField,
          value: bulkValue,
        }
        : {
          itemIds: bulkSelectedIds,
          field: bulkEditField,
          value: bulkValue,
        };
      const res = await axios.post('/api/non-certified-diamonds/bulk-update', payload);
      showSuccess(`Updated ${res.data.updated} item(s).`);
      setBulkEditConfirmOpen(false);
      closeBulkEdit();
      fetchLots();
    } catch (err) {
      setBulkEditError(err.response?.data?.error || 'Bulk update failed');
    } finally {
      setBulkEditLoading(false);
    }
  };

  const bulkEditFieldLabel = bulkEditField === 'allowOrderOverStock'
    ? t('settings.inventory.lots.allowOrderOverStockOverride')
    : (bulkEditField === 'buyPricePerCarat'
      ? t('inventory.col.buyPricePerCarat')
      : t('inventory.col.pricePerCarat'));

  const bulkEditValueLabel = bulkEditField === 'allowOrderOverStock'
    ? (bulkEditValue === 'true'
      ? t('settings.inventory.lots.overrideAllow')
      : (bulkEditValue === 'false'
        ? t('settings.inventory.lots.overrideDisallow')
        : t('settings.inventory.lots.overrideDefault')))
    : String(bulkEditValue);

  const lotAllowsOrderOverStock = useCallback((lot) => {
    if (!lot) return false;
    if (isClient) return lot.allowOrderOverStock === true;
    return resolveLotAllowOrderOverStock(lot, lotsSettings);
  }, [isClient, lotsSettings]);

  const lotCanAddToCart = useCallback((lot) => {
    if (!lot) return false;
    return lot.availableCarat > 0 || lotAllowsOrderOverStock(lot);
  }, [lotAllowsOrderOverStock]);

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('inventory.nonCertifiedDiamonds')}
        </Typography>
        {(canAddDiamond || canEditDiamond) && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {canAddDiamond && (
              <Button variant="contained" size="small" onClick={() => setAddLotDialogOpen(true)} startIcon={<AddIcon />}>
                {t('inventory.addLot')}
              </Button>
            )}
            {(canAddDiamond || canEditDiamond) && !isMobile && (
              <Button
                variant="outlined" size="small" startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                {t('inventory.uploadCsv')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="More filters (availability, width…)">
            <IconButton
              color="primary"
              onClick={() => setFiltersDrawerOpen(true)}
              aria-label="Open lot filters"
              size="large"
              sx={{ flexShrink: 0 }}
            >
              <Badge color="error" badgeContent={lotsFiltersActiveCount} invisible={lotsFiltersActiveCount === 0}>
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <QuickFilterBar
            shapes={filterShapeList}
            weightLabel="Ct/stone"
            weightTolerance={0.20}
            lengthTolerance={0.50}
            shapeValue={qfShape}
            weightCenterValue={qfWeight}
            lengthCenterValue={qfLength}
            includeAllShapeOption={!isClient}
            defaultShape={clientDefaultShape}
            onApply={handleQuickLotFilterApply}
            onClear={handleQuickLotFilterClear}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inventory.filterHintDesktop')}
        </Typography>
        {activeLotsChips.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {activeLotsChips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                onDelete={isClient && chip.key === 'shape' ? undefined : () => handleRemoveLotChip(chip.key)}
              />
            ))}
          </Box>
        )}
        {bulkEditOpen && isInternal && isManager && (
          <Box sx={{ mt: 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            {bulkEditError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setBulkEditError('')}>{bulkEditError}</Alert>}
            {bulkEditStep === 1 ? (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t('inventory.bulkEditStep1Title')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('inventory.bulkEditStep1Desc')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => setBulkSelectedIds(lots.map((lot) => lot._id))}>{t('inventory.selectAllOnPage')}</Button>
                  <Button size="small" variant="outlined" onClick={() => setBulkSelectedIds([])}>{t('inventory.clearSelection')}</Button>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>({bulkSelectedIds.length} selected)</Typography>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                  <Button size="small" variant="contained" onClick={handleBulkEditUseFilter}>
                    {t('inventory.updateAllMatching', { count: lotsTotal })}
                  </Button>
                  <Box sx={{ flex: 1, minWidth: 80 }} />
                  <Button size="small" variant="contained" onClick={handleBulkEditNext} disabled={!bulkEditUseFilter && bulkSelectedIds.length === 0}>
                    {t('inventory.nextSetLotPrice')}
                  </Button>
                  <Button size="small" onClick={closeBulkEdit}>{t('inventory.cancel')}</Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t('inventory.bulkEditLotsStep2Title')}</Typography>
                <Typography variant="body2" color="text.secondary">{bulkEditCount} item(s) will be updated. Use the dialog to choose the new value, then confirm.</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Button size="small" variant="outlined" onClick={() => { setBulkEditStep(1); setBulkEditField(''); setBulkEditValue(''); }}>{t('inventory.back')}</Button>
                  <Button size="small" onClick={closeBulkEdit}>{t('inventory.cancel')}</Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      <Box sx={{ position: 'relative', minHeight: lotsLoading ? 200 : undefined }} aria-busy={lotsLoading}>
        {lotsLoading && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.72)'), backdropFilter: 'blur(2px)', borderRadius: 1 }}>
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ opacity: lotsLoading ? 0.35 : 1, transition: 'opacity 0.2s ease', pointerEvents: lotsLoading ? 'none' : 'auto' }}>
          {isMobile ? (
            /* ── Mobile card list ── */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <MobileTableSort
                columns={lotsSortColumns}
                sortBy={lotsSortBy}
                sortOrder={lotsSortDir}
                onSort={handleLotsSort}
                defaultSortBy=""
                defaultSortOrder="asc"
                onResetSort={handleLotsSortReset}
              />
              {!lotsLoading && lots.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 1 }}>No lots found.</Typography>
              ) : lots.map((lot) => {
                const measurement = lot.length != null && lot.width != null ? `${lot.length}×${lot.width}` : (lot.measurement || '—');
                const availableLabel = isClient
                  ? formatLotAvailableForClient(lot.availableCarat, lotsSettings)
                  : `${lot.availableCarat} ct`;
                const pricePerCtLabel = lot.pricePerCarat != null ? `$${lot.pricePerCarat.toLocaleString()}` : '—';
                return (
                  <Paper key={lot._id} variant="outlined" sx={{ p: isClient ? 1 : 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isClient ? 0.25 : 1, gap: 0.5 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {isClient ? (
                          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.25, fontSize: '0.875rem' }}>
                            {getNonCertLotClientCardTitle(lot)}
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                              {lot.sku || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {lot.shape} · {measurement}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        {canEditDiamond && (
                          <Tooltip title={t('inventory.editLot')}>
                            <IconButton size="small" onClick={() => openEditLot(lot)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAddToCart && lotCanAddToCart(lot) && (
                          <Tooltip title={t('inventory.addToCart')}>
                            <IconButton size="small" color="primary" onClick={() => handleOpenAddLotToCartDialog(lot)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                              <AddShoppingCartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isInternal && (
                          <Tooltip title={t('inventory.auditLog')}>
                            <IconButton size="small" onClick={() => openAuditLog('lot', lot._id, `${lot.shape || ''} ${measurement}`.trim() || 'Lot')}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    {isClient ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                        Price/ct:{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {pricePerCtLabel}
                        </Box>
                        {' · '}
                        Available: {availableLabel}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Ct/stone</Typography>
                          <Typography variant="body2" fontWeight={500}>{lot.caratPerStone != null ? lot.caratPerStone : '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Price/ct</Typography>
                          <Typography variant="body2" fontWeight={500}>${lot.pricePerCarat?.toLocaleString()}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Available</Typography>
                          <Typography variant="body2" fontWeight={500}>{availableLabel}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          ) : (
            /* ── Desktop table ── */
            <TableContainer component={Paper} sx={{ mb: 2, maxHeight: VIEWPORT_TABLE_MAX_HEIGHT, overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    {bulkEditOpen && isInternal && isManager && (
                      <TableCell sx={{ backgroundColor: 'background.paper', width: 48 }} />
                    )}
                    {!isClient && (
                      <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('inventory.col.sku')}</TableCell>
                    )}
                    {sortCell('shape', t('inventory.col.shape'))}
                    {sortCell('length', t('inventory.col.measurement'))}
                    {sortCell('caratPerStone', 'Ct/stone')}
                    {sortCell('pricePerCarat', t('inventory.col.pricePerCarat'))}
                    {sortCell('availableCarat', t('inventory.col.available'))}
                    <TableCell sx={{ position: 'sticky', right: 0, top: 0, backgroundColor: 'background.paper', zIndex: 3, boxShadow: '-2px 0 8px rgba(0,0,0,0.08)', whiteSpace: 'nowrap' }}>
                      {t('inventory.col.reserveAction')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!lotsLoading && lots.length === 0 ? (
                    <TableRow><TableCell colSpan={isClient ? 6 : 7 + (bulkEditOpen && isInternal && isManager ? 1 : 0)}>No non-certified diamonds. Add one using the button above.</TableCell></TableRow>
                  ) : (
                    lots.map((lot) => (
                      <TableRow key={lot._id}>
                        {bulkEditOpen && isInternal && isManager && (
                          <TableCell>
                            <Checkbox
                              size="small"
                              checked={bulkSelectedIds.includes(lot._id)}
                              onChange={(e) => {
                                if (e.target.checked) setBulkSelectedIds((prev) => [...prev, lot._id]);
                                else setBulkSelectedIds((prev) => prev.filter((id) => id !== lot._id));
                              }}
                            />
                          </TableCell>
                        )}
                        {!isClient && <TableCell>{lot.sku || '—'}</TableCell>}
                        <TableCell>{lot.shape}</TableCell>
                        <TableCell>{lot.length != null && lot.width != null ? `${lot.length}×${lot.width}` : (lot.measurement || '—')}</TableCell>
                        <TableCell>{lot.caratPerStone != null ? lot.caratPerStone : '—'}</TableCell>
                        <TableCell>${lot.pricePerCarat?.toLocaleString()}</TableCell>
                        <TableCell>
                          {isClient
                            ? (showAvailableToClients
                              ? formatLotAvailableForClient(lot.availableCarat, lotsSettings)
                              : '—')
                            : lot.availableCarat}
                        </TableCell>
                        <TableCell sx={{ position: 'sticky', right: 0, backgroundColor: 'background.paper', zIndex: 1, boxShadow: '-2px 0 8px rgba(0,0,0,0.08)', whiteSpace: 'nowrap' }}>
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            {canEditDiamond && (
                              <Tooltip title={t('inventory.editLot')}>
                                <IconButton size="small" onClick={() => openEditLot(lot)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canAddToCart && lotCanAddToCart(lot) && (
                              <Tooltip title={t('inventory.addToCart')}>
                                <IconButton size="small" color="primary" onClick={() => handleOpenAddLotToCartDialog(lot)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                                  <AddShoppingCartIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isInternal && (
                              <Tooltip title={t('inventory.auditLog')}>
                                <IconButton size="small" onClick={() => openAuditLog('lot', lot._id, `${lot.shape || ''} ${lot.length != null && lot.width != null ? `${lot.length}×${lot.width}` : lot.measurement || ''}`.trim() || 'Lot')}>
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={lotsTotal}
            page={lotsPage}
            onPageChange={(e, newPage) => setLotsPage(newPage)}
            rowsPerPage={lotsRowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setLotsRowsPerPage(next);
              setLotsPage(0);
              saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.inventoryLots, next).catch(() => {});
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      </Box>

      <LotsFilterDrawer
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        appliedFilters={appliedLotsFilters}
        shapeList={filterShapeList}
        onApply={handleApplyLotFilters}
        onClearAll={handleClearLotFilters}
        isManager={isInternal && isManager}
        onBulkEdit={handleOpenBulkEditFromFilters}
        includeAllShapeOption={!isClient}
        defaultShape={clientDefaultShape}
      />

      {/* Upload CSV dialog (non-certified) */}
      <UploadCsvDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        defaultUploadType="nonCertified"
        onImportSuccess={() => fetchLots()}
      />

      <ReserveLotDialog
        open={reserveLotDialogOpen}
        lot={reserveLot}
        isClient={isClient}
        allowOrderOverStock={reserveLot ? lotAllowsOrderOverStock(reserveLot) : false}
        onClose={handleCloseAddLotToCartDialog}
        onConfirm={handleAddLotToCart}
      />

      <AddLotDialog
        open={addLotDialogOpen}
        onClose={() => setAddLotDialogOpen(false)}
        onSaved={handleAddLotSaved}
        shapeOptions={formShapeList}
        isMobile={isMobile}
      />

      <EditLotDialog
        open={!!editLot}
        lot={editLot}
        onClose={closeEditLot}
        onSaved={handleEditLotSaved}
        shapeOptions={formShapeList}
        canSeeBuyPrices={canSeeBuyPrices}
        isMobile={isMobile}
      />

      <Dialog
        open={bulkEditOpen && bulkEditStep === 2}
        onClose={() => { setBulkEditStep(1); setBulkEditField(''); setBulkEditValue(''); }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{t('inventory.bulkEditLotsDialogTitle')}</DialogTitle>
        <DialogContent>
          {bulkEditError && <Alert severity="error" sx={{ mb: 2 }}>{bulkEditError}</Alert>}
          <RadioGroup
            row
            value={bulkEditField}
            onChange={(e) => { setBulkEditField(e.target.value); setBulkEditValue(''); }}
            sx={{ mb: 2 }}
          >
            <FormControlLabel value="pricePerCarat" control={<Radio />} label={t('inventory.col.pricePerCarat')} />
            {canSeeBuyPrices && (
              <FormControlLabel value="buyPricePerCarat" control={<Radio />} label={t('inventory.col.buyPricePerCarat')} />
            )}
            <FormControlLabel
              value="allowOrderOverStock"
              control={<Radio />}
              label={t('settings.inventory.lots.allowOrderOverStockOverride')}
            />
          </RadioGroup>
          {(bulkEditField === 'pricePerCarat' || bulkEditField === 'buyPricePerCarat') && (
            <TextField
              fullWidth
              label={bulkEditFieldLabel}
              type="number"
              value={bulkEditValue}
              onChange={(e) => setBulkEditValue(e.target.value)}
              inputProps={{ step: 0.01, min: 0 }}
            />
          )}
          {bulkEditField === 'allowOrderOverStock' && (
            <SelectField
              label={bulkEditFieldLabel}
              fullWidth
              value={bulkEditValue}
              onChange={setBulkEditValue}
              options={[
                { value: 'default', label: t('settings.inventory.lots.overrideDefault') },
                { value: 'true', label: t('settings.inventory.lots.overrideAllow') },
                { value: 'false', label: t('settings.inventory.lots.overrideDisallow') },
              ]}
              emptyLabel=""
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkEditStep(1); setBulkEditField(''); setBulkEditValue(''); setBulkEditError(''); }}>{t('inventory.back')}</Button>
          <Button onClick={closeBulkEdit}>{t('inventory.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleBulkEditFieldConfirm}
            disabled={
              !bulkEditField
              || (bulkEditField === 'allowOrderOverStock' ? !bulkEditValue : (bulkEditValue === '' || Number(bulkEditValue) < 0))
            }
          >
            {t('inventory.nextConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkEditConfirmOpen} onClose={() => setBulkEditConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('inventory.confirmBulkUpdate')}</DialogTitle>
        <DialogContent>
          <Typography>{t('inventory.confirmUpdateItems', { count: bulkEditCount })}</Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>{bulkEditFieldLabel}:</strong> {bulkEditValueLabel}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditConfirmOpen(false)}>{t('inventory.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkEditSubmit} disabled={bulkEditLoading}>
            {bulkEditLoading ? t('inventory.updating') : t('inventory.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit log dialog */}
      <AuditLogDialog
        open={auditDialogOpen}
        onClose={() => { setAuditDialogOpen(false); setAuditLogs([]); setAuditTitle(''); }}
        logs={auditLogs}
        loading={auditLoading}
        title={auditTitle}
      />
    </>
  );
};

export default React.memo(LotsInventory, inventoryTabPropsAreEqual);
